import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Key, EnvelopeSimple, Lock, ArrowRight, Warning, ShieldCheck } from '@phosphor-icons/react';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Что-то пошло не так. Попробуйте снова.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ==================== KEY GATE PAGE ====================
export const KeyGatePage = () => {
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { verifyKey } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await verifyKey(key);
      navigate('/login');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]" data-testid="key-gate-page">
      <div className="w-full max-w-sm space-y-6 p-8 animate-fade-in">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 border border-[#262626] bg-[#141414] mb-6">
            <Key size={32} weight="bold" className="text-[#FACC15]" />
          </div>
          <h1 className="text-2xl font-black tracking-tight text-[#FAFAFA]">
            ВВЕДИТЕ КЛЮЧ
          </h1>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 border border-red-500/50 bg-red-500/10 text-red-400" data-testid="key-error">
            <Warning size={18} weight="bold" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Ключ доступа"
              className="bg-[#141414] border-[#262626] text-[#FAFAFA] placeholder:text-[#525252] rounded-none h-12 focus:border-[#FACC15] focus:ring-0 text-center tracking-widest"
              required
              autoFocus
              data-testid="key-input"
            />
          </div>
          <Button
            type="submit"
            disabled={isLoading || !key.trim()}
            className="w-full h-12 bg-[#FACC15] hover:bg-[#EAB308] text-[#0A0A0A] font-semibold rounded-none transition-colors duration-200"
            data-testid="key-submit-btn"
          >
            {isLoading ? 'Проверка...' : 'Далее'}
            {!isLoading && <ArrowRight className="ml-2" size={20} weight="bold" />}
          </Button>
        </form>
      </div>
    </div>
  );
};


// ==================== LOGIN PAGE ====================
export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (result.requires_2fa) {
        navigate('/2fa');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]" data-testid="login-page">
      <div className="w-full max-w-sm space-y-6 p-8 animate-fade-in">
        <div className="text-center">
          <h1 className="text-3xl font-black tracking-tight text-[#FAFAFA]">
            DOCFLOW
          </h1>
          <p className="mt-2 text-[#A3A3A3] text-sm">
            Войдите в свой аккаунт
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 border border-red-500/50 bg-red-500/10 text-red-400" data-testid="login-error">
            <Warning size={18} weight="bold" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">
              Email
            </Label>
            <div className="relative">
              <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={20} />
              <Input
                id="email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                className="pl-10 bg-[#141414] border-[#262626] text-[#FAFAFA] placeholder:text-[#525252] rounded-none h-12 focus:border-[#FACC15] focus:ring-0"
                required
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">
              Пароль
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={20} />
              <Input
                id="password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-10 bg-[#141414] border-[#262626] text-[#FAFAFA] placeholder:text-[#525252] rounded-none h-12 focus:border-[#FACC15] focus:ring-0"
                required
                data-testid="login-password-input"
              />
            </div>
          </div>

          <Button
            type="submit" disabled={isLoading}
            className="w-full h-12 bg-[#FACC15] hover:bg-[#EAB308] text-[#0A0A0A] font-semibold rounded-none transition-colors duration-200"
            data-testid="login-submit-btn"
          >
            {isLoading ? 'Вход...' : 'Далее'}
            {!isLoading && <ArrowRight className="ml-2" size={20} weight="bold" />}
          </Button>
        </form>
      </div>
    </div>
  );
};


// ==================== 2FA PAGE ====================
export const TwoFactorPage = () => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { verify2FA, pendingLogin, logout } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await verify2FA(code);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    logout();
    navigate('/');
  };

  if (!pendingLogin) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]" data-testid="2fa-page">
      <div className="w-full max-w-sm space-y-6 p-8 animate-fade-in">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 border border-[#262626] bg-[#141414] mb-6">
            <ShieldCheck size={32} weight="bold" className="text-[#FACC15]" />
          </div>
          <h1 className="text-2xl font-black tracking-tight text-[#FAFAFA]">
            ДВУХФАКТОРНАЯ
          </h1>
          <p className="mt-2 text-[#A3A3A3] text-sm">
            Введите код из Google Authenticator
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 border border-red-500/50 bg-red-500/10 text-red-400" data-testid="2fa-error">
            <Warning size={18} weight="bold" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="bg-[#141414] border-[#262626] text-[#FAFAFA] placeholder:text-[#525252] rounded-none h-14 text-center text-2xl tracking-[0.5em] font-mono focus:border-[#FACC15] focus:ring-0"
              required
              autoFocus
              data-testid="2fa-code-input"
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading || code.length !== 6}
            className="w-full h-12 bg-[#FACC15] hover:bg-[#EAB308] text-[#0A0A0A] font-semibold rounded-none transition-colors duration-200"
            data-testid="2fa-submit-btn"
          >
            {isLoading ? 'Проверка...' : 'Подтвердить'}
            {!isLoading && <ShieldCheck className="ml-2" size={20} weight="bold" />}
          </Button>

          <button
            type="button"
            onClick={handleCancel}
            className="w-full text-center text-[#525252] text-xs hover:text-[#A3A3A3] transition-colors"
            data-testid="2fa-cancel-btn"
          >
            Отмена
          </button>
        </form>
      </div>
    </div>
  );
};
